import { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  getIdTokenResult
} from 'firebase/auth';
import { 
  getFunctions, 
  httpsCallable 
} from 'firebase/functions';
import firebaseConfig from '../firebase-config';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const functions = getFunctions(app);

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await getIdTokenResult(user);
          setUser(user);
          setClaims(token.claims);
          setError(null);
        } catch (err) {
          console.error('Error getting user claims:', err);
          setError(err.message);
        }
      } else {
        setUser(null);
        setClaims(null);
        setError(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email, password) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signUp = async (email, password, displayName) => {
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Call Cloud Function to create user document
      const createUserAccount = httpsCallable(functions, 'createUserAccount');
      await createUserAccount({ email, password, displayName });
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const createAdminUser = async (email, password) => {
    try {
      setError(null);
      const createAdminUserFn = httpsCallable(functions, 'createAdminUser');
      const result = await createAdminUserFn({ email, password });
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const getUserRole = async (userId = null) => {
    try {
      setError(null);
      const getUserRoleFn = httpsCallable(functions, 'getUserRole');
      const result = await getUserRoleFn({ userId });
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    user,
    loading,
    error,
    isAdmin: claims?.admin === true,
    role: claims?.role || 'user',
    permissions: claims?.permissions || [],
    signIn,
    signUp,
    logout,
    createAdminUser,
    getUserRole
  };
};
